import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { updateTable, getGroups, createGroup, removeGroup, updateGroup } from '../../services/api';
import FloorPlanCanvas from './FloorPlanCanvas';
import FloorPlanSidebar from './FloorPlanSidebar';
import CreateGroupModal from './CreateGroupModal';
import GroupDetailModal from './GroupDetailModal';
import HelpIcon from '../help/HelpIcon';
import './FloorPlanEditor.css';

function FloorPlanEditor({
  tables,
  onTablesChange,
  onEditTable,
  onGenerateQR,
  onCloseTable,
  onShowQR,
  onPrintQR,
}) {
  const { t } = useTranslation();
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [placingTable, setPlacingTable] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Group state
  const [groups, setGroups] = useState([]);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedTableNumbers, setSelectedTableNumbers] = useState(new Set());
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);

  // Load groups
  useEffect(() => {
    const loadGroups = async () => {
      try {
        const data = await getGroups();
        setGroups(data.groups || []);
      } catch (error) {
        console.error('Error loading groups:', error);
      }
    };
    loadGroups();
  }, [tables]); // Reload when tables change

  // Extract rooms
  const rooms = useMemo(() => {
    const locationSet = new Set();
    tables.forEach((table) => {
      if (table.location) locationSet.add(table.location);
    });
    return Array.from(locationSet).sort();
  }, [tables]);

  const hasRooms = rooms.length > 1 || (rooms.length === 1 && tables.some((t) => !t.location));

  // Filter tables by room
  const filteredTables = useMemo(() => {
    if (!selectedRoom) return tables;
    return tables.filter((table) => table.location === selectedRoom);
  }, [tables, selectedRoom]);

  // Tables that have positions (for canvas)
  const positionedTables = useMemo(() => {
    return filteredTables.filter((t) => t.positionX != null && t.positionY != null);
  }, [filteredTables]);

  const savePosition = useCallback(
    async (tableNumber, positionX, positionY, extraFields = {}) => {
      setSaving(true);
      try {
        await updateTable(tableNumber, {
          positionX: Math.round(positionX * 100) / 100,
          positionY: Math.round(positionY * 100) / 100,
          ...extraFields,
        });
        onTablesChange();
      } catch (error) {
        console.error('Error saving position:', error);
      } finally {
        setSaving(false);
      }
    },
    [onTablesChange]
  );

  const handleTableDragEnd = useCallback(
    (tableNumber, pctX, pctY) => {
      savePosition(tableNumber, pctX, pctY);
    },
    [savePosition]
  );

  const handleCanvasClick = useCallback(
    (pctX, pctY) => {
      if (!placingTable) return;
      savePosition(placingTable.tableNumber, pctX, pctY);
      setPlacingTable(null);
    },
    [placingTable, savePosition]
  );

  const handleSidebarSelect = useCallback((table) => {
    setPlacingTable((prev) => (prev?.tableNumber === table.tableNumber ? null : table));
  }, []);

  const handleTableContextMenu = useCallback((table, e) => {
    e.preventDefault?.();
    setContextMenu({
      table,
      x: e.clientX,
      y: e.clientY,
    });
  }, []);

  const handleRemoveFromPlan = useCallback(async () => {
    if (!contextMenu) return;
    setSaving(true);
    try {
      await updateTable(contextMenu.table.tableNumber, {
        positionX: null,
        positionY: null,
      });
      onTablesChange();
    } catch (error) {
      console.error('Error removing from plan:', error);
    } finally {
      setSaving(false);
      setContextMenu(null);
    }
  }, [contextMenu, onTablesChange]);

  const handleChangeShape = useCallback(
    async (shape) => {
      if (!contextMenu) return;
      setSaving(true);
      try {
        await updateTable(contextMenu.table.tableNumber, { shape });
        onTablesChange();
      } catch (error) {
        console.error('Error changing shape:', error);
      } finally {
        setSaving(false);
        setContextMenu(null);
      }
    },
    [contextMenu, onTablesChange]
  );

  // Group handlers
  const handleTableSelect = useCallback((tableNumber) => {
    setSelectedTableNumbers((prev) => {
      const next = new Set(prev);
      if (next.has(String(tableNumber))) {
        next.delete(String(tableNumber));
      } else {
        next.add(String(tableNumber));
      }
      return next;
    });
  }, []);

  const handleToggleSelectionMode = useCallback(() => {
    setSelectionMode((prev) => {
      if (prev) {
        setSelectedTableNumbers(new Set());
      }
      return !prev;
    });
  }, []);

  const handleCreateGroup = useCallback(
    async (name) => {
      try {
        await createGroup({ name, tableNumbers: Array.from(selectedTableNumbers) });
        setShowGroupModal(false);
        setSelectionMode(false);
        setSelectedTableNumbers(new Set());
        onTablesChange();
      } catch (error) {
        console.error('Error creating group:', error);
        const msg = error.response?.data?.error || error.message;
        alert(msg);
      }
    },
    [selectedTableNumbers, onTablesChange]
  );

  const handleDissolveGroup = useCallback(
    async (groupId) => {
      try {
        await removeGroup(groupId);
        setContextMenu(null);
        setSelectedGroup(null);
        onTablesChange();
      } catch (error) {
        console.error('Error dissolving group:', error);
        const msg = error.response?.data?.error || error.message;
        alert(msg);
      }
    },
    [onTablesChange]
  );

  const handleGroupClick = useCallback((group) => {
    setSelectedGroup(group);
  }, []);

  const handleGroupUpdate = useCallback(
    async (groupId, data) => {
      try {
        await updateGroup(groupId, data);
        setSelectedGroup(null);
        onTablesChange();
      } catch (error) {
        console.error('Error updating group:', error);
        const msg = error.response?.data?.error || error.message;
        alert(msg);
      }
    },
    [onTablesChange]
  );

  return (
    <div className="fp-editor" onClick={() => setContextMenu(null)}>
      {/* Toolbar: room tabs + selection mode */}
      <div className="fp-toolbar">
        {hasRooms && (
          <div className="fp-room-tabs">
            <button
              type="button"
              className={`fp-room-tab ${!selectedRoom ? 'fp-room-tab--active' : ''}`}
              onClick={() => setSelectedRoom(null)}
            >
              {t('waiter.allRooms', 'Todos')}
            </button>
            {rooms.map((room) => (
              <button
                type="button"
                key={room}
                className={`fp-room-tab ${selectedRoom === room ? 'fp-room-tab--active' : ''}`}
                onClick={() => setSelectedRoom(room)}
              >
                {room}
              </button>
            ))}
          </div>
        )}
        <div className="fp-toolbar-actions">
          <HelpIcon topicKey="floorplan.overview" />
          <button
            type="button"
            className={`btn btn-sm ${selectionMode ? 'btn-primary' : 'btn-secondary'}`}
            onClick={handleToggleSelectionMode}
            title={
              !selectionMode
                ? t('tables.selectionModeTooltip', 'Activa modo selección para agrupar mesas')
                : undefined
            }
          >
            {selectionMode
              ? t('tables.cancelSelection', 'Cancelar')
              : t('tables.selectionMode', 'Seleccionar')}
          </button>
          <HelpIcon topicKey="floorplan.grouping" placement="left" />
        </div>
      </div>

      {/* Instruction banner when selection mode active and < 2 tables selected */}
      {selectionMode && selectedTableNumbers.size < 2 && (
        <div className="fp-selection-instructions" role="status">
          {t(
            'tables.selectionModeInstructions',
            'Hacé clic en las mesas del plano para seleccionarlas. Seleccioná 2 o más para crear un grupo.'
          )}
        </div>
      )}

      {/* Status legend */}
      <div className="fp-status-legend" aria-label={t('tables.statusLegend', 'Leyenda de estados')}>
        <div className="fp-legend-item">
          <span className="fp-legend-dot fp-legend-dot--available" />
          <span>{t('tables.available')}</span>
        </div>
        <div className="fp-legend-item">
          <span className="fp-legend-dot fp-legend-dot--occupied" />
          <span>{t('tables.occupied')}</span>
        </div>
        <div className="fp-legend-item">
          <span className="fp-legend-dot fp-legend-dot--reserved" />
          <span>{t('tables.reserved')}</span>
        </div>
        <div className="fp-legend-item">
          <span className="fp-legend-dot fp-legend-dot--maintenance" />
          <span>{t('tables.maintenance')}</span>
        </div>
      </div>

      <div className="fp-editor-body">
        <FloorPlanCanvas
          tables={positionedTables}
          onTableDragEnd={handleTableDragEnd}
          onCanvasClick={handleCanvasClick}
          onTableContextMenu={handleTableContextMenu}
          placingTable={placingTable}
          groups={groups}
          selectedTableNumbers={selectedTableNumbers}
          selectionMode={selectionMode}
          onTableSelect={handleTableSelect}
          onGroupClick={handleGroupClick}
        />
        <FloorPlanSidebar
          tables={filteredTables}
          placingTable={placingTable}
          onTableSelect={handleSidebarSelect}
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed((prev) => !prev)}
        />
      </div>

      {/* Floating bar when 2+ tables selected */}
      {selectionMode && selectedTableNumbers.size >= 2 && (
        <div className="fp-selection-bar">
          <span>
            {selectedTableNumbers.size} {t('tables.selectTablesForGroup', 'mesas seleccionadas')}
          </span>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => setShowGroupModal(true)}
          >
            {t('tables.createGroup', 'Crear grupo')}
          </button>
        </div>
      )}

      {saving && (
        <div className="fp-saving-indicator">
          {t('tables.positionSaved', 'Posicion guardada')}...
        </div>
      )}

      {/* Context menu */}
      {contextMenu && (
        <div
          className="fp-context-menu"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="fp-context-menu-title">
            {t('tables.table')} {contextMenu.table.tableNumber}
          </div>
          <div className="fp-context-menu-section">
            <span className="fp-context-menu-label">{t('tables.shape', 'Forma')}</span>
            <div className="fp-context-menu-shapes">
              <button
                type="button"
                className={`fp-shape-btn ${(contextMenu.table.shape || 'square') === 'square' ? 'active' : ''}`}
                onClick={() => handleChangeShape('square')}
                title={t('tables.shapeSquare', 'Cuadrada')}
              >
                <span className="fp-shape-icon fp-shape-icon--square" />
              </button>
              <button
                type="button"
                className={`fp-shape-btn ${contextMenu.table.shape === 'round' ? 'active' : ''}`}
                onClick={() => handleChangeShape('round')}
                title={t('tables.shapeRound', 'Redonda')}
              >
                <span className="fp-shape-icon fp-shape-icon--round" />
              </button>
              <button
                type="button"
                className={`fp-shape-btn ${contextMenu.table.shape === 'rect' ? 'active' : ''}`}
                onClick={() => handleChangeShape('rect')}
                title={t('tables.shapeRect', 'Rectangular')}
              >
                <span className="fp-shape-icon fp-shape-icon--rect" />
              </button>
            </div>
          </div>
          <div className="fp-context-menu-divider" />
          {onEditTable && (
            <button
              type="button"
              className="fp-context-menu-item"
              onClick={() => {
                onEditTable(contextMenu.table);
                setContextMenu(null);
              }}
            >
              {t('tables.editTable')}
            </button>
          )}
          {onGenerateQR && (
            <button
              type="button"
              className="fp-context-menu-item"
              onClick={() => {
                onGenerateQR(contextMenu.table.tableNumber);
                setContextMenu(null);
              }}
            >
              {contextMenu.table.qrCode
                ? t('tables.regenerateQR', 'Regenerar QR')
                : t('tables.generateQR')}
            </button>
          )}
          {onShowQR && contextMenu.table.qrCode && (
            <button
              type="button"
              className="fp-context-menu-item"
              onClick={() => {
                onShowQR(contextMenu.table);
                setContextMenu(null);
              }}
            >
              {t('tables.viewQR', 'Ver QR')}
            </button>
          )}
          {onPrintQR && contextMenu.table.qrCode && (
            <button
              type="button"
              className="fp-context-menu-item"
              onClick={() => {
                onPrintQR(contextMenu.table);
                setContextMenu(null);
              }}
            >
              {t('tables.printQR', 'Imprimir QR')}
            </button>
          )}
          {onCloseTable && contextMenu.table.currentSessionId && (
            <button
              type="button"
              className="fp-context-menu-item fp-context-menu-item--warning"
              onClick={() => {
                onCloseTable(contextMenu.table);
                setContextMenu(null);
              }}
            >
              {t('tables.closeTable', 'Cerrar mesa')}
            </button>
          )}
          <div className="fp-context-menu-divider" />
          {contextMenu.table.groupId && (
            <button
              type="button"
              className="fp-context-menu-item fp-context-menu-item--danger"
              onClick={() => handleDissolveGroup(contextMenu.table.groupId)}
            >
              {t('tables.dissolveGroup', 'Disolver grupo')}
            </button>
          )}
          <button
            type="button"
            className="fp-context-menu-item fp-context-menu-item--danger"
            onClick={handleRemoveFromPlan}
          >
            {t('tables.removeFromPlan', 'Quitar del plano')}
          </button>
        </div>
      )}

      {/* Create group modal */}
      {showGroupModal && (
        <CreateGroupModal
          selectedTableNumbers={selectedTableNumbers}
          onConfirm={handleCreateGroup}
          onCancel={() => setShowGroupModal(false)}
        />
      )}

      {/* Group detail modal */}
      {selectedGroup && (
        <GroupDetailModal
          group={selectedGroup}
          tables={tables}
          onUpdate={handleGroupUpdate}
          onDissolve={handleDissolveGroup}
          onClose={() => setSelectedGroup(null)}
        />
      )}
    </div>
  );
}

export default FloorPlanEditor;
